<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import BusinessDay from '../../inbox/components/BusinessDay.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  defaultTimeSlot,
  timeSlotParse,
  timeSlotTransform,
  timeZoneOptions,
} from '../../inbox/helpers/businessHour';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const enabled = ref(false);
const timezone = ref('Asia/Karachi');
const unavailableMessage = ref('');
const holidayMessage = ref('');
const breakMessage = ref('');
const timeSlots = ref([...defaultTimeSlot]);
const overrides = ref([]);
const maxActiveChats = ref(0);
// Break slots per weekday, e.g. { 5: [{ start: '13:00', end: '14:00' }] } for Friday prayer.
const breaks = ref({});
const saving = ref(false);

const dayNames = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

const team = computed(() => store.getters['teams/getTeam'](route.params.teamId));
const timezones = computed(() => timeZoneOptions());
const hasError = computed(
  () => enabled.value && timeSlots.value.some(slot => slot.from && !slot.valid)
);

const toTimeValue = (hour, minutes) =>
  `${String(hour ?? 0).padStart(2, '0')}:${String(minutes ?? 0).padStart(2, '0')}`;

const setDefaults = () => {
  const availability = team.value?.availability || {};
  maxActiveChats.value = team.value?.max_active_chats_per_agent || 0;
  enabled.value = availability.working_hours_enabled || false;
  timezone.value = availability.timezone || 'Asia/Karachi';
  unavailableMessage.value = availability.out_of_office_message || '';
  holidayMessage.value = availability.holiday_message || '';
  breakMessage.value = availability.break_message || '';
  timeSlots.value = availability.working_hours?.length
    ? timeSlotParse(availability.working_hours)
    : [...defaultTimeSlot];
  breaks.value = Object.fromEntries(
    (availability.working_hours || []).map(entry => [
      entry.day_of_week,
      (entry.breaks || []).map(item => ({
        start: toTimeValue(item.start_hour, item.start_minutes),
        end: toTimeValue(item.end_hour, item.end_minutes),
      })),
    ])
  );
  overrides.value = (availability.holiday_overrides || []).map(item => ({
    ...item,
    open_time: toTimeValue(item.open_hour, item.open_minutes),
    close_time: toTimeValue(item.close_hour, item.close_minutes),
  }));
};

const updateSlot = (day, data) => {
  timeSlots.value = timeSlots.value.map(item =>
    item.day === day ? data : item
  );
};

const addOverride = () => {
  overrides.value.push({
    date: '',
    label: '',
    message: '',
    closed_all_day: true,
    open_all_day: false,
    open_time: '09:00',
    close_time: '17:00',
  });
};

const removeOverride = index => overrides.value.splice(index, 1);

const addBreak = day => {
  breaks.value[day] = [...(breaks.value[day] || []), { start: '13:00', end: '14:00' }];
};

const removeBreak = (day, index) => breaks.value[day].splice(index, 1);

const serializeWorkingHours = () =>
  timeSlotTransform(timeSlots.value).map(entry => ({
    ...entry,
    breaks: (breaks.value[entry.day_of_week] || [])
      .filter(item => item.start && item.end && item.end > item.start)
      .map(item => {
        const from = splitTime(item.start);
        const to = splitTime(item.end);
        return {
          start_hour: from.hour,
          start_minutes: from.minutes,
          end_hour: to.hour,
          end_minutes: to.minutes,
        };
      }),
  }));

const splitTime = value => {
  const [hour, minutes] = String(value || '00:00').split(':').map(Number);
  return { hour, minutes };
};

const serializeOverrides = () =>
  overrides.value
    .filter(item => item.date)
    .map(item => {
      const opening = splitTime(item.open_time);
      const closing = splitTime(item.close_time);
      return {
        date: item.date,
        label: item.label,
        message: item.message || '',
        closed_all_day: item.closed_all_day,
        open_all_day: item.open_all_day,
        open_hour: opening.hour,
        open_minutes: opening.minutes,
        close_hour: closing.hour,
        close_minutes: closing.minutes,
      };
    });

const save = async () => {
  saving.value = true;
  try {
    await store.dispatch('teams/update', {
      id: route.params.teamId,
      working_hours_enabled: enabled.value,
      timezone: timezone.value,
      out_of_office_message: unavailableMessage.value,
      holiday_message: holidayMessage.value,
      break_message: breakMessage.value,
      working_hours: serializeWorkingHours(),
      holiday_overrides: serializeOverrides(),
      max_active_chats_per_agent: Number(maxActiveChats.value) || 0,
    });
    useAlert(t('TEAMS_SETTINGS.AVAILABILITY.SUCCESS'));
    await router.replace({
      name: 'settings_teams_edit_members',
      params: {
        page: 'edit',
        teamId: route.params.teamId,
      },
    });
  } catch (error) {
    useAlert(t('TEAMS_SETTINGS.AVAILABILITY.ERROR'));
  } finally {
    saving.value = false;
  }
};

onMounted(async () => {
  if (!team.value?.id) await store.dispatch('teams/get');
  setDefaults();
});
</script>

<template>
  <div class="h-full w-full p-8 col-span-6">
    <h2 class="text-xl font-medium text-n-slate-12">
      {{ $t('TEAMS_SETTINGS.AVAILABILITY.TITLE') }}
    </h2>
    <p class="mt-1 mb-6 text-sm text-n-slate-11">
      {{ $t('TEAMS_SETTINGS.AVAILABILITY.DESCRIPTION') }}
    </p>

    <form class="grid gap-6" @submit.prevent="save">
      <label class="grid gap-2">
        <span>{{ $t('TEAMS_SETTINGS.AVAILABILITY.MAX_ACTIVE_CHATS') }}</span>
        <input
          v-model.number="maxActiveChats"
          type="number"
          min="0"
          class="w-40 rounded border border-n-weak p-2"
        />
        <span class="text-sm text-n-slate-11">
          {{ $t('TEAMS_SETTINGS.AVAILABILITY.MAX_ACTIVE_CHATS_HELP') }}
        </span>
      </label>

      <label class="flex items-center gap-2">
        <input v-model="enabled" type="checkbox" />
        <span>{{ $t('TEAMS_SETTINGS.AVAILABILITY.ENABLE') }}</span>
      </label>

      <template v-if="enabled">
        <label class="grid gap-2">
          <span>{{ $t('TEAMS_SETTINGS.AVAILABILITY.TIMEZONE') }}</span>
          <multiselect
            v-model="timezone"
            :options="timezones.map(item => item.value)"
            :custom-label="value => timezones.find(item => item.value === value)?.label || value"
            :allow-empty="false"
          />
        </label>

        <label class="grid gap-2">
          <span>{{ $t('TEAMS_SETTINGS.AVAILABILITY.MESSAGE') }}</span>
          <textarea
            v-model="unavailableMessage"
            rows="4"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-2 p-3"
          />
        </label>

        <label class="grid gap-2">
          <span>{{ $t('TEAMS_SETTINGS.AVAILABILITY.BREAK_MESSAGE') }}</span>
          <textarea
            v-model="breakMessage"
            rows="3"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-2 p-3"
          />
          <span class="text-sm text-n-slate-11">
            {{ $t('TEAMS_SETTINGS.AVAILABILITY.BREAK_MESSAGE_HELP') }}
          </span>
        </label>

        <div>
          <h3 class="mb-2 font-medium">
            {{ $t('TEAMS_SETTINGS.AVAILABILITY.WEEKLY') }}
          </h3>
          <div v-for="slot in timeSlots" :key="slot.day">
            <BusinessDay
              :day-name="dayNames[slot.day]"
              :time-slot="slot"
              @update="data => updateSlot(slot.day, data)"
            />
            <div v-if="slot.valid || slot.openAllDay" class="grid gap-2 pb-3 pl-8">
              <div
                v-for="(item, index) in breaks[slot.day] || []"
                :key="index"
                class="flex items-center gap-2 text-sm"
              >
                <span class="w-12 text-n-slate-11">
                  {{ $t('TEAMS_SETTINGS.AVAILABILITY.BREAK') }}
                </span>
                <input v-model="item.start" type="time" class="rounded border border-n-weak p-1" />
                <span>-</span>
                <input v-model="item.end" type="time" class="rounded border border-n-weak p-1" />
                <NextButton
                  type="button"
                  color="ruby"
                  size="xs"
                  :label="$t('TEAMS_SETTINGS.AVAILABILITY.REMOVE')"
                  @click="removeBreak(slot.day, index)"
                />
              </div>
              <NextButton
                type="button"
                link
                size="xs"
                class="w-fit"
                :label="$t('TEAMS_SETTINGS.AVAILABILITY.ADD_BREAK')"
                @click="addBreak(slot.day)"
              />
            </div>
          </div>
        </div>

        <div class="grid gap-3">
          <label class="grid gap-2">
            <span>{{ $t('TEAMS_SETTINGS.AVAILABILITY.HOLIDAY_MESSAGE') }}</span>
            <textarea
              v-model="holidayMessage"
              rows="4"
              class="w-full rounded-lg border border-n-weak bg-n-alpha-2 p-3"
            />
            <span class="text-sm text-n-slate-11">
              {{ $t('TEAMS_SETTINGS.AVAILABILITY.HOLIDAY_MESSAGE_HELP') }}
            </span>
          </label>

          <div class="flex items-center justify-between">
            <div>
              <h3 class="font-medium">
                {{ $t('TEAMS_SETTINGS.AVAILABILITY.HOLIDAYS') }}
              </h3>
              <p class="text-sm text-n-slate-11">
                {{ $t('TEAMS_SETTINGS.AVAILABILITY.HOLIDAYS_HELP') }}
              </p>
            </div>
            <NextButton
              type="button"
              :label="$t('TEAMS_SETTINGS.AVAILABILITY.ADD_HOLIDAY')"
              @click="addOverride"
            />
          </div>

          <div
            v-for="(override, index) in overrides"
            :key="index"
            class="grid grid-cols-1 md:grid-cols-7 gap-3 rounded-lg border border-n-weak p-4"
          >
            <input v-model="override.date" type="date" class="rounded border border-n-weak p-2" />
            <input v-model="override.label" type="text" :placeholder="$t('TEAMS_SETTINGS.AVAILABILITY.HOLIDAY_NAME')" class="rounded border border-n-weak p-2" />
            <input v-model="override.message" type="text" :placeholder="$t('TEAMS_SETTINGS.AVAILABILITY.HOLIDAY_CUSTOM_MESSAGE')" class="rounded border border-n-weak p-2" />
            <label class="flex items-center gap-2">
              <input v-model="override.closed_all_day" type="checkbox" />
              {{ $t('TEAMS_SETTINGS.AVAILABILITY.CLOSED') }}
            </label>
            <input v-model="override.open_time" type="time" :disabled="override.closed_all_day" class="rounded border border-n-weak p-2" />
            <input v-model="override.close_time" type="time" :disabled="override.closed_all_day" class="rounded border border-n-weak p-2" />
            <NextButton type="button" color="ruby" :label="$t('TEAMS_SETTINGS.AVAILABILITY.REMOVE')" @click="removeOverride(index)" />
          </div>
        </div>
      </template>

      <NextButton
        type="submit"
        :label="$t('TEAMS_SETTINGS.AVAILABILITY.SAVE')"
        :is-loading="saving"
        :disabled="hasError"
      />
    </form>
  </div>
</template>
